import parse from "html-react-parser"
import { useTranslation } from "react-i18next"

import { CourseList } from "@/features/training/components/course-list"
import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { CourseSection } from "@/features/training/components/layouts/course-section"
import { ensureArray } from "@/features/training/utils/ensure-array"

export function AIAgentsBuildPage() {
  const { t } = useTranslation("", { keyPrefix: "training.ai-agents.build" })
  const list = <T = string,>(key: string) => ensureArray<T>(t(key, { returnObjects: true }))

  return (
    <CourseLayout>
      <CourseSection>
        <h1>{t("title")}</h1>
        <h2>{t("purpose.title")}</h2>
        <p>{parse(t("purpose.description"))}</p>
        <p>
          <strong>{t("purpose.what_you_gain")}</strong>
        </p>
        <CourseList data={list("purpose.benefits")} />
        <p>
          <em>{t("purpose.level")}</em>
        </p>
      </CourseSection>

      <CourseSection>
        <h2>{t("learning_objectives.title")}</h2>
        <p>{parse(t("learning_objectives.description"))}</p>
        <CourseList data={list("learning_objectives.objectives")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("overview.title")}</h2>
        <p>{parse(t("overview.description"))}</p>
      </CourseSection>

      <CourseSection>
        <h2>{t("building_agents.title")}</h2>
        <p>{parse(t("building_agents.description"))}</p>
        <div className="my-4 flex justify-center">
          <div className="border-border rounded-lg border p-2 shadow-sm">
            <img
              alt="Building agents overview"
              className="h-auto max-w-2xl rounded-xl"
              src="/assets/training_images/ai-agents/02-01.avif"
            />
          </div>
        </div>

        <h3>{t("building_agents.code_based.title")}</h3>
        <p>{parse(t("building_agents.code_based.description"))}</p>

        <h3>{t("building_agents.no_code.title")}</h3>
        <p>{parse(t("building_agents.no_code.description"))}</p>
        <p>
          <strong>{t("building_agents.no_code.key_features")}</strong>
        </p>
        <CourseList data={list("building_agents.no_code.features")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("platforms.title")}</h2>
        <p>{parse(t("platforms.description"))}</p>

        <h3>{t("platforms.copilot_studio.title")}</h3>
        <p>{parse(t("platforms.copilot_studio.description"))}</p>
        <CourseList data={list("platforms.copilot_studio.features")} />
        <div className="my-4 flex justify-center">
          <div className="border-border rounded-lg border p-2 shadow-sm">
            <img
              alt="Microsoft Copilot Studio"
              className="h-auto max-w-2xl rounded-xl"
              src="/assets/training_images/ai-agents/02-02_MS.avif"
            />
          </div>
        </div>
        <p className="text-muted-foreground text-sm">{parse(t("platforms.copilot_studio.note"))}</p>

        <h3>{t("platforms.n8n.title")}</h3>
        <p>{parse(t("platforms.n8n.description"))}</p>
        <div className="my-4 flex justify-center">
          <div className="border-border rounded-lg border p-2 shadow-sm">
            <img
              alt="n8n workflow automation"
              className="h-auto max-w-2xl rounded-xl"
              src="/assets/training_images/ai-agents/02-03_n8n.avif"
            />
          </div>
        </div>
        <p>
          <strong>{t("platforms.n8n.example_label")}</strong> {t("platforms.n8n.example")}
        </p>

        <h3>{t("platforms.other.title")}</h3>
        <CourseList data={list("platforms.other.alternatives")} />
        <blockquote className="border-warning bg-warning/15 border-l-4 p-4">
          <p>{parse(t("platforms.other.warning"))}</p>
        </blockquote>
      </CourseSection>

      <CourseSection>
        <h2>{t("examples.title")}</h2>
        {list<{ title: string; points: string[]; effect_label: string; effect: string }>(
          "examples.use_cases"
        ).map((useCase, index) => (
          <div key={index} className="mb-6">
            <h3>{useCase.title}</h3>
            <CourseList data={ensureArray(useCase.points)} />
            <p>
              <strong>{useCase.effect_label}</strong> {useCase.effect}
            </p>
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("exercises.title")}</h2>
        {list<{ title: string; steps: string[] }>("exercises.items").map((exercise, index) => (
          <div key={index} className="mb-6">
            <h3>{exercise.title}</h3>
            <CourseList data={ensureArray(exercise.steps)} />
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("pitfalls.title")}</h2>
        <CourseList data={list("pitfalls.tips")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("summary.title")}</h2>
        <CourseList data={list("summary.points")} />
        <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
          <p>{parse(t("summary.policy_reminder"))}</p>
        </blockquote>
        <p>{parse(t("summary.next_module"))}</p>
      </CourseSection>
    </CourseLayout>
  )
}
